import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api.js';
import { FIELD_ORDER, LIST_FIELDS, OBJECT_FIELDS, validWebsite } from '../../companyOptions.js';

// The company profile form, shared by "Profil de l'entreprise" and
// "Coordonnées": both edit parts of the same profile and save it whole to
// the existing review (PUT /membership/company). While changes are under
// review both pages are locked; a rejection reason shows on both.

const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
export const textToHtml = (text) => String(text || '').split(/\n{2,}/).map((p) => p.trim()).filter(Boolean)
  .map((p) => `<p>${esc(p).replace(/\n/g, '<br>')}</p>`).join('');

function toForm(src) {
  const f = Object.fromEntries(FIELD_ORDER.map((k) => [k, LIST_FIELDS.includes(k) ? [...(src[k] || [])] : OBJECT_FIELDS.includes(k) ? { ...(src[k] || {}) } : src[k] ?? '']));
  f.country ||= 'CA';
  f.year_established ??= '';
  if (!f.description_html && f.description) f.description_html = textToHtml(f.description);
  f.sectors = f.sectors.map(String);
  return f;
}

// "+1 438 664 0000" from what was typed: 10 digits = North America.
export function formatIntlPhone(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';
  const d = s.replace(/\D/g, '');
  if (!s.startsWith('+') && d.length === 10) return `+1 ${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
  if (d.length === 11 && d[0] === '1') return `+1 ${d.slice(1, 4)} ${d.slice(4, 7)} ${d.slice(7)}`;
  if (s.startsWith('+')) return `+${d}`.replace(/^(\+\d{1,3})(\d{3})(\d{3})(\d+)$/, '$1 $2 $3 $4');
  return s;
}

export function useCompanyForm() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [status, setStatus] = useState({ kind: '', text: '' });
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const apply = useCallback((d) => { setData(d); setForm(toForm(d.draft || d.profile)); setDirty(false); }, []);
  useEffect(() => { api('/membership/company').then(apply).catch((err) => setStatus({ kind: 'error', text: err.code || 'server_error', code: err.code })); }, [apply]);

  const update = (changes) => { setForm((f) => ({ ...f, ...(typeof changes === 'function' ? changes(f) : changes) })); setDirty(true); };
  const set = (k) => (e) => update({ [k]: e.target.value });
  const locked = data?.review.status === 'pending';

  async function save() {
    if (!validWebsite(String(form.website || '').trim())) return { error: 'invalid_website' };
    setBusy(true); setStatus({ kind: '', text: '' });
    try {
      const r = await api('/membership/company', { method: 'PUT', body: { ...form, year_established: form.year_established === '' ? null : form.year_established } });
      apply(r);
      return { result: r.result };
    } catch (err) {
      return { error: err.code || 'server_error', err };
    } finally {
      setBusy(false);
    }
  }
  return { data, setData, form, update, set, save, busy, locked, status, setStatus, dirty };
}
